import { useEffect, useMemo, useState } from 'react';

import { DEFAULT_FIRST_PAGE } from 'v2/constants';
import { useDebounce } from 'v2/hooks/useDebounce';

interface UseRewardsHistorySearchArgs {
  userId: string;
  onPageChange: (page: number) => void;
}

interface UseRewardsHistorySearchReturn {
  searchValue: string;
  setSearchValue: (value: string) => void;
  swrParams: {
    userId: string;
    value?: string;
  };
}

export const useRewardsHistorySearch = ({
  userId,
  onPageChange,
}: UseRewardsHistorySearchArgs): UseRewardsHistorySearchReturn => {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 400);
  const [activeSearchValue, setActiveSearchValue] = useState('');

  useEffect(() => {
    if (debouncedSearchValue.length >= 3 && debouncedSearchValue !== activeSearchValue) {
      setActiveSearchValue(debouncedSearchValue);
      onPageChange(DEFAULT_FIRST_PAGE);
      return;
    }

    if (debouncedSearchValue === '' && activeSearchValue !== '') {
      setActiveSearchValue('');
      onPageChange(DEFAULT_FIRST_PAGE);
    }
  }, [activeSearchValue, debouncedSearchValue, onPageChange]);

  const swrParams = useMemo(
    () => ({
      userId,
      ...(activeSearchValue !== '' ? { value: activeSearchValue } : {}),
    }),
    [activeSearchValue, userId]
  );

  return { searchValue, setSearchValue, swrParams };
};
